/**
 * I18n Service
 * 
 * Service for fetching and caching EspoCRM translations.
 * Provides functions to translate entity names, field names, and other labels.
 * This is a generic service that can be used across all tenants.
 */

// Import MetadataApi to extend it
import { MetadataApi } from './metadataService';

// I18nApi extends MetadataApi to include I18n endpoints
export interface I18nApi extends MetadataApi {
  endpoints: MetadataApi['endpoints'] & {
    getI18n?: {
      initiate: (arg: { default: boolean }, options?: { forceRefetch?: boolean }) => any;
    };
  };
}

// Cache for translations (per API instance)
const translationsCache = new WeakMap<I18nApi, Record<string, any>>();
const translationsPromise = new WeakMap<I18nApi, Promise<Record<string, any>>>();

/**
 * Fetch translations from EspoCRM I18n API
 */
export async function fetchTranslations(api: I18nApi): Promise<Record<string, any>> {
  // Return cached translations if available
  const cached = translationsCache.get(api);
  if (cached) {
    return cached;
  }

  // Return existing promise if fetch is in progress
  const existingPromise = translationsPromise.get(api);
  if (existingPromise) {
    return existingPromise;
  }

  // If API doesn't have I18n endpoint, return empty object
  if (!api.endpoints.getI18n) {
    return {};
  }

  // Create new fetch promise using RTK Query
  const promise = (async () => {
    try {
      // Lazy import store to avoid circular dependency
      const { store } = await import('src/store');
      const result = await store.dispatch(
        api.endpoints.getI18n!.initiate({ default: true }, { forceRefetch: true })
      ) as any;

      if (result.error) {
        console.warn('Failed to fetch translations:', result.error);
        return {};
      }

      if (!result.data) {
        console.warn('Translations data is undefined');
        return {};
      }

      translationsCache.set(api, result.data);
      return result.data;
    } catch (error) {
      translationsPromise.delete(api); // Reset promise on error
      console.error('Error fetching translations:', error);
      return {};
    }
  })();

  translationsPromise.set(api, promise);
  return promise;
}

/**
 * Get translations (from cache or fetch)
 */
export async function getTranslations(api: I18nApi): Promise<Record<string, any>> {
  const cached = translationsCache.get(api);
  if (cached) {
    return cached;
  }
  return fetchTranslations(api);
}

/**
 * Translate an entity name
 * @param entityName - The entity name to translate
 * @param translations - The translations object (optional, will fetch if not provided)
 */
export async function translateEntityName(
  api: I18nApi,
  entityName: string,
  translations?: Record<string, any>
): Promise<string> {
  const i18n = translations || await getTranslations(api);
  
  // Try different possible keys for entity translation
  const possibleKeys = [
    `Global.scopeNames.${entityName}`,
    `Global.scopeNamesPlural.${entityName}`,
    `scopeNames.${entityName}`,
    `scopeNamesPlural.${entityName}`,
    `Entity.${entityName}`,
    `Entities.${entityName}`,
  ];

  for (const key of possibleKeys) {
    const value = getNestedValue(i18n, key);
    if (value && typeof value === 'string') {
      return value;
    }
  }

  // Return original name if no translation found
  return entityName;
}

/**
 * Translate a field name
 * @param entityName - The entity name
 * @param fieldName - The field name to translate
 * @param translations - The translations object (optional, will fetch if not provided)
 */
export async function translateFieldName(
  api: I18nApi,
  entityName: string,
  fieldName: string,
  translations?: Record<string, any>
): Promise<string> {
  const i18n = translations || await getTranslations(api);
  
  // Try different possible keys for field translation
  const possibleKeys = [
    `${entityName}.fields.${fieldName}`,
    `${entityName}.fieldLabels.${fieldName}`,
    `fields.${fieldName}`,
    `fieldLabels.${fieldName}`,
    `Global.fieldLabels.${fieldName}`,
  ];

  for (const key of possibleKeys) {
    const value = getNestedValue(i18n, key);
    if (value && typeof value === 'string') {
      return value;
    }
  }

  // Return original name if no translation found
  return fieldName;
}

/**
 * Translate a panel label
 * @param entityName - The entity name
 * @param panelLabel - The panel label to translate
 * @param translations - The translations object (optional, will fetch if not provided)
 */
export async function translatePanelLabel(
  api: I18nApi,
  entityName: string,
  panelLabel: string,
  translations?: Record<string, any>
): Promise<string> {
  const i18n = translations || await getTranslations(api);
  
  // Try different possible keys for panel translation
  const possibleKeys = [
    `${entityName}.labels.${panelLabel}`,
    `${entityName}.panels.${panelLabel}`,
    `labels.${panelLabel}`,
    `panels.${panelLabel}`,
  ];

  for (const key of possibleKeys) {
    const value = getNestedValue(i18n, key);
    if (value && typeof value === 'string') {
      return value;
    }
  }

  // Return original label if no translation found
  return panelLabel;
}

/**
 * Get nested value from object using dot notation
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

/**
 * Clear translations cache (useful for testing or refresh)
 */
export function clearTranslationsCache(api: I18nApi): void {
  translationsCache.delete(api);
  translationsPromise.delete(api);
}

/**
 * Initialize translations service
 */
export async function initializeTranslationsService(api: I18nApi): Promise<Record<string, any>> {
  return fetchTranslations(api);
}
